import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// constants
import routesConfig from 'constants/routesConfig.json';
import { TabSlug, TAB_SLUG } from 'constants/ecoachTabs';

// components
import Card from 'eCoach/allChats/Card';
import ConfirmDialog from 'shared/ConfirmDialog';
import EmptyState from 'eCoach/allChats/EmptyState';
// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';
import {
  useECoachSessions,
  useHideSession,
  ECoachSession,
} from 'shared/hooks/api/ecoach';

// styles
import * as Cards from 'eCoach/_styles/Cards.style';

interface AllChatsProps {
  activeSession: string | undefined;
  activeView: TabSlug;
  setActiveSession: (session: ECoachSession | null) => void;
  resetChatGuideData: () => void;
}

export default function AllChats({
  activeSession,
  activeView,
  setActiveSession,
}: AllChatsProps): React.ReactElement {
  const { data: conversations = [] } = useECoachSessions();
  const { mutate: hideSession } = useHideSession();
  const { t } = useTranslation('pageEcoach');
  const history = useHistory();
  const chatCapture = usePosthogCapture();

  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>();

  const closeDeleteModal = () => setIsOpenConfirmDialog(false);
  const openDeleteModal = (e: React.MouseEvent, pastSession: ECoachSession) => {
    e.stopPropagation();
    setSessionId(pastSession.id);
    setIsOpenConfirmDialog(true);
  };

  const handleHideSession = () => {
    if (sessionId) {
      hideSession(sessionId);
      chatCapture.deleted();
      if (sessionId === activeSession) setActiveSession(null);
      closeDeleteModal();
    }
  };

  const getSession = useCallback(
    (id: string) => {
      history.push(`${routesConfig.CONVO_PAGE}/${id}`);
    },
    [history]
  );

  const ongoingChats = conversations.filter(
    (chat) => chat.resolution_status === 'active'
  );

  const draftChats = conversations.filter(
    (chat) => chat.resolution_status === 'draft'
  );

  const otherChats = conversations.filter(
    (chat) => !['active', 'draft'].includes(chat.resolution_status)
  );

  return (
    <>
      {activeView === TAB_SLUG.ALL && (
        <Cards.MultiCardSection className="demo-tour-step-6">
          {conversations.length > 0 ? (
            conversations.map((chat, i) => (
              <Card
                chat={chat}
                getSession={getSession}
                index={i}
                key={chat.id}
                openDeleteModal={openDeleteModal}
              />
            ))
          ) : (
            <EmptyState tab={TAB_SLUG.ALL} />
          )}
        </Cards.MultiCardSection>
      )}

      {activeView === TAB_SLUG.IN_PROGRESS && (
        <Cards.MultiCardSection className="demo-tour-step-6">
          {ongoingChats.length > 0 ? (
            ongoingChats.map((chat, i) => (
              <Card
                chat={chat}
                getSession={getSession}
                index={i}
                key={chat.id}
                openDeleteModal={openDeleteModal}
              />
            ))
          ) : (
            <EmptyState tab={TAB_SLUG.IN_PROGRESS} />
          )}
        </Cards.MultiCardSection>
      )}

      {activeView === TAB_SLUG.RESOLVED && (
        <Cards.MultiCardSection>
          {otherChats.length > 0 ? (
            otherChats.map((chat, i) => (
              <Card
                chat={chat}
                getSession={getSession}
                index={i}
                key={chat.id}
                openDeleteModal={openDeleteModal}
              />
            ))
          ) : (
            <EmptyState tab={TAB_SLUG.RESOLVED} />
          )}
        </Cards.MultiCardSection>
      )}

      {activeView === TAB_SLUG.DRAFTS && (
        <Cards.MultiCardSection>
          {draftChats.length > 0 ? (
            draftChats.map((chat, i) => (
              <Card
                chat={chat}
                getSession={getSession}
                index={i}
                key={chat.id}
                openDeleteModal={openDeleteModal}
              />
            ))
          ) : (
            <EmptyState tab={TAB_SLUG.DRAFTS} />
          )}
        </Cards.MultiCardSection>
      )}

      {isOpenConfirmDialog && (
        <ConfirmDialog
          cancelLabel={t('No')}
          successLabel={t('Yes')}
          successCallback={handleHideSession}
          contentText={t(
            'allChats.Are you sure you want to delete this session?'
          )}
          handleClose={closeDeleteModal}
          isOpen={true}
        />
      )}
    </>
  );
}
